/**
 * The band at the top of a screen that says which part of Wend you are in, in
 * a sentence — slice 65, from the concept's `.eyebrow` and `.lede`.
 *
 * When the old Reach split into Reach (Meta ads) and Connect (email and
 * WhatsApp), a person landing on `/campaigns` or `/reach/ads` needed to be told
 * which pillar they were standing in and what it is for, once, at the top, in
 * the same shape on every pillar's screens. That is all this is: a small mono
 * eyebrow naming the pillar, an optional one-line lede saying what it does, and
 * an optional slot for the screen's actions.
 *
 * **It is not a heading, and it renders no `h1`.** The top bar in `AppShell`
 * owns the one `h1` on every screen — the page title — and a second `h1` here
 * would give the page two names, one of them "REACH". The eyebrow is a `p`: it
 * is context for the title, not a title. The rejected alternative was to move
 * the `h1` down into this band, which would have changed every screen's
 * outline at once and left the top bar's title as decoration; the pillar name
 * is the smaller claim, so it takes the smaller element.
 *
 * Colour rules this file keeps, because each was a defect somewhere else:
 *
 *   - The eyebrow is `text-brand-b`. That clears contrast against the page
 *     ground (`ink`) and **only** there — inside a card or on `surface-2` it
 *     does not, so this belongs at the top of a screen and not inside a panel.
 *   - The rule before it is `bg-brand-b/60`, not the concept's gradient. The
 *     gradient is the brand's loudest mark, verify-sense-screens counts
 *     `bg-brand-gradient` on the Sense screens, and a 22px hairline repeated on
 *     every page would spend it on decoration.
 *   - The lede is `text-muted`, never the faint token (theme.css: faint is not a
 *     text colour, and verify-dashboard counts its one permitted use).
 *   - The lede stops at 66ch, a readable line, whatever the column's width.
 *
 * The icon and the rule are `aria-hidden`: a screen reader hears the eyebrow's
 * word, which already says everything the glyph does.
 */
import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';

import { cn } from '../lib/cn';

export interface PageHeaderProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  /** The pillar or area this screen belongs to — a word or two, "Connect". */
  eyebrow: ReactNode;
  /** Decorative glyph beside the eyebrow; the pillar's icon. */
  icon?: ReactNode;
  /** One sentence on what this part of Wend is for. Keep it to a line. */
  lede?: ReactNode;
  /** The screen's actions, right-aligned on wide screens — at most one primary. */
  actions?: ReactNode;
}

export const PageHeader = forwardRef<HTMLDivElement, PageHeaderProps>(function PageHeader(
  { eyebrow, icon, lede, actions, className, ...rest },
  ref,
) {
  const hasLede = lede !== undefined && lede !== null;
  const hasActions = actions !== undefined && actions !== null;

  return (
    <div
      ref={ref}
      className={cn('mb-6 flex flex-wrap items-end justify-between gap-x-6 gap-y-4', className)}
      {...rest}
    >
      <div className="min-w-0">
        <p className="flex items-center gap-2.5 font-mono text-[0.7rem] font-medium uppercase tracking-[0.16em] text-brand-b">
          <span aria-hidden="true" className="h-px w-[22px] shrink-0 bg-brand-b/60" />
          {icon !== undefined && icon !== null && (
            <span
              aria-hidden="true"
              className="grid size-4 shrink-0 place-items-center [&_svg:not([class*='size-'])]:size-3.5"
            >
              {icon}
            </span>
          )}
          <span>{eyebrow}</span>
        </p>
        {hasLede && (
          <p className="mt-2 max-w-[66ch] text-[0.95rem] leading-relaxed text-muted">{lede}</p>
        )}
      </div>
      {hasActions && <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
});
